<template>
  <section
    ref="rootRef"
    :class="DS_FEATURE_HINT_CLASSES.Root"
    role="dialog"
    tabindex="-1"
    :aria-labelledby="titleId"
    :aria-describedby="props.text ? textId : undefined"
    :data-hint-key="props.hintKey"
    data-cy="ds-feature-hint"
  >
    <DSFeatureHintMedia
      v-if="props.media"
      :src="props.media"
      :alt="props.mediaAlt"
    />

    <div :class="DS_FEATURE_HINT_CLASSES.Content">
      <header :class="DS_FEATURE_HINT_CLASSES.Header">
        <DSBadge
          v-if="props.badge"
          v-bind="badgeProps"
          data-cy="ds-feature-hint__badge"
        />
        <h3
          :id="titleId"
          :class="DS_FEATURE_HINT_CLASSES.Title"
          data-cy="ds-feature-hint__title"
        >
          {{ props.title }}
        </h3>
      </header>

      <p
        v-if="props.text"
        :id="textId"
        :class="DS_FEATURE_HINT_CLASSES.Text"
        data-cy="ds-feature-hint__text"
      >
        {{ props.text }}
      </p>

      <slot />
    </div>

    <footer :class="DS_FEATURE_HINT_CLASSES.Actions">
      <DSButton
        v-if="props.learnMoreUrl"
        variant="tertiary"
        size="sm"
        :href="props.learnMoreUrl"
        target="_blank"
        rel="noopener"
        suffix-icon="box-arrow-top-right"
        data-cy="ds-feature-hint__learn-more"
        @click="emit('dismiss', DS_FEATURE_HINT_DISMISS_REASON.LearnMore)"
      >
        {{ labels.buttonLearnMore }}
      </DSButton>
      <DSButton
        variant="primary"
        size="sm"
        data-cy="ds-feature-hint__got-it"
        @click="emit('dismiss', DS_FEATURE_HINT_DISMISS_REASON.GotIt)"
      >
        {{ labels.buttonGotIt }}
      </DSButton>
    </footer>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, useId } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import DSBadge from "@/src/design_system/data_display/badge/components/DSBadge.vue";
import { DS_BADGE_STATE } from "@/src/design_system/data_display/badge/types/state";
import { DS_SIZE } from "@/src/design_system/shared/types/size";
import DSFeatureHintMedia from "@/src/design_system/feedback/feature_hint/components/DSFeatureHintMedia.vue";
import type { DSFeatureHintCardProps } from "@/src/design_system/feedback/feature_hint/types/cardProps";
import {
  DS_FEATURE_HINT_DISMISS_REASON,
  type DSFeatureHintCardEmits,
} from "@/src/design_system/feedback/feature_hint/types/emits";
import { DS_FEATURE_HINT_CLASSES } from "@/src/design_system/feedback/feature_hint/constants/classes";

const props = withDefaults(defineProps<DSFeatureHintCardProps>(), {
  text: undefined,
  media: undefined,
  mediaAlt: undefined,
  learnMoreUrl: undefined,
  badge: undefined,
});

const emit = defineEmits<DSFeatureHintCardEmits>();

const rootRef = ref<HTMLElement>();

const id = useId();
const titleId = `${id}-title`;
const textId = `${id}-text`;

const labels = window.I18n.designSystem.featureHint;
const badgeProps = computed(() => ({ state: DS_BADGE_STATE.Accent, size: DS_SIZE.Large, ...props.badge }));

const focus = (options?: FocusOptions) => rootRef.value?.focus(options);

defineExpose({ focus });
</script>
